import React, {useState} from 'react';
import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from 'modules';
import {setEditJobAction} from 'modules/setEditorJob';
import {setWebEditorJobAction} from 'modules/setWebEditorJob';
import {
  CUT_LIMIT_DURATION,
  BJ_AFREECATV_DOMAIN,
  CUT_MIN_DURATION,
  SECTION_EDITOR_VERSION,
} from 'constants/config';
import {IVodInfoData} from 'lib/api/vodInfo';
import {setClickLog, getLoginId, mergeDedupe} from 'lib/utils';
import {getTotalShadeDuration, getTotalEditDuration} from 'lib/playUtils';
import ModalPortal from 'ModalPortal';
import Title from 'components/common/Title';
import Loading from 'components/common/modal/Loading';
import {t} from 'lib/i18n';

/**
 * 영상 제목/저장 컨테이너
 */

const initWebEditSaveInfo = {
  bbs: 0,
  category: '',
  vod_category: '',
  title: '',
  contents: '',
  hotissue: 'N',
  strmLangType: '',
};

const TitleContainer = () => {
  const dispatch = useDispatch();
  const {vodData, fileList, cut, editMode} = useSelector(
    ({vodInfo, file, cut, ui}: RootState) => ({
      vodData: vodInfo.titleFileInfo.data,
      fileList: file.list,
      cut,
      editMode: ui.editMode,
    }),
  );
  const {data, loading, error} = useSelector(
    ({setEditorJob}: RootState) => setEditorJob.savePost,
  );
  const {
    data: webData,
    loading: webLoading,
    error: webEditError,
  } = useSelector(({setWebEditorJob}: RootState) => setWebEditorJob.savePost);
  const [modal, setModal] = useState(false);
  const [saveSuccessModal, setSaveSuccessModal] = useState(false);
  const [saveBtn, setSaveBtn] = useState('off');
  const [webEditSaveInfo, setWebEditSaveInfo] = useState(initWebEditSaveInfo); // 웹 편집기 모드에서 유저가 입력하는 데이터

  const {response} = vodData as IVodInfoData;

  /**
   * 구간 편집기
   * 영상 저장 ajax 처리 후 오는 함수
   */
  useEffect(() => {
    if (data) {
      const {RESULT, MSG} = data;
      switch (RESULT) {
        case 1:
          setSaveSuccessModal(true); // 영상 저장 setState
          break;
        default:
          alert(MSG);
          break;
      }
    }
  }, [data, loading, error]);

  /**
   * 웹 편집기
   * 영상 저장 ajax 처리 후 오는 함수
   */
  useEffect(() => {
    if (webData) {
      const {RESULT, MSG} = webData;
      switch (RESULT) {
        case 1: {
          const loginId = getLoginId(); // 로그인 ID
          const bPageMove = window.confirm(
            t(
              '편집영상이 등록 되었습니다.\n방송국에서 바로 확인 하시겠습니까?',
            ),
          );

          setWebEditSaveInfo(initWebEditSaveInfo); // 초기화

          if (bPageMove) {
            window.open(`${BJ_AFREECATV_DOMAIN}/${loginId}`, '_blank');
          }
          break;
        }
        default:
          alert(MSG);
          break;
      }
    }
  }, [webData, webLoading, webEditError]);

  /**
   * '영상 저장' 버튼 활성화/비활성화 여부 체크
   * 1. 컷 편집 모드 일 때 비활성화
   * 2-1. 구간 편집기 > 그림자 영역이 0이면(편집 영역 없을 경우) 비활성화
   * 2-2. 웹 편집기 > 편집 영역이 0이면 비활성화
   */
  useEffect(() => {
    switch (editMode) {
      case 'section': {
        const totalShadeDuration = getTotalShadeDuration(cut.shadeDuration);

        setSaveBtnWrap(totalShadeDuration);
        break;
      }
      case 'web': {
        const totalDuration = getTotalEditDuration(cut.lists);

        setSaveBtnWrap(totalDuration);
        break;
      }
      default:
        break;
    }
  }, [cut.shadeDuration, cut.status, cut.lists]);

  /**
   * 영상 저장 버튼 on/off 함수
   * @param totalDuration 편집 시간
   */
  const setSaveBtnWrap = (totalDuration: number) => {
    if (totalDuration === 0 || cut.status) {
      setSaveBtn('off');
    } else {
      setSaveBtn('');
    }
  };

  // 영상 저장 모달 창 출력
  const onClickModal = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();

    // 비활성화 상태면 리턴
    if (saveBtn === 'off') return;

    let totalDuration = 0;
    switch (editMode) {
      case 'section':
        totalDuration = getTotalShadeDuration(cut.shadeDuration);
        break;
      case 'web':
        totalDuration = getTotalEditDuration(cut.lists);

        if (totalDuration < CUT_MIN_DURATION) {
          alert(
            `${t('{{CUT_MIN_DURATION}}초 이상 편집 가능 합니다.', {placeholder: {CUT_MIN_DURATION: CUT_MIN_DURATION}})}`,
          );
          return;
        }
        break;
      default:
        alert(t('잠시 후 다시 시도해주십시오.(mode)'));
        return;
    }
    if (totalDuration > CUT_LIMIT_DURATION) {
      alert(
        `${t('{{CUT_LIMIT_DURATION}}분 내로 편집 가능 합니다.', {placeholder: {CUT_LIMIT_DURATION: CUT_LIMIT_DURATION / 60}})}`,
      );
      return;
    }

    setModal(true);
  };
  // 영상 저장 취소 버튼 클릭
  const onClickSaveCancel = () => {
    setModal(false);
  };

  /**
   * 영상 저장 클릭
   */
  const onClickSave = () => {
    if (!fileList) {
      alert(t('편집 대상 원본 파일이 없습니다.'));
      return;
    }

    const edit_type = editMode === 'section' ? 0 : 1; // 편집 모드

    let shade_duration = 0;
    if (editMode === 'section') {
      shade_duration = getTotalShadeDuration(cut.shadeDuration);
      if (shade_duration <= 0) {
        alert(t('컷 편집 구간이 없습니다.'));
        return;
      }
    } else {
      const {bbs, category, vod_category, title, strmLangType} =
        webEditSaveInfo;
      if (
        bbs <= 0 ||
        category === '' ||
        vod_category === '' ||
        title === '' ||
        strmLangType === ''
      ) {
        alert(t('필수 사항을 입력해주세요.'));
        return;
      }
    }

    const {title_no, broad_no} = response.info; // 영상에 대한 정보
    // const shade_duration = cut.shadeDuration; // 컷 편집한 시간
    const mergeData = mergeDedupe(cut.lists);
    const saveData = [];
    for (let i = 0; i < mergeData.length; i++) {
      if (!mergeData[i]) {
        // undefined OR duration=0인거 제외
        continue;
      }

      saveData.push(mergeData[i]);
    }

    if (saveData.length <= 0) {
      alert(t('편집할 구간이 없습니다.'));
      return;
    }

    setModal(false);

    if (editMode === 'section') {
      dispatch(
        setEditJobAction.request({
          edit_job_info: saveData,
          edit_type,
          title_no,
          broad_no,
          shade_duration,
          shade_area: cut.shades,
          version: SECTION_EDITOR_VERSION,
        }),
      );
    } else {
      dispatch(
        setWebEditorJobAction.request({
          edit_job_info: cut.lists,
          edit_type,
          title_no,
          broad_no,
          info: webEditSaveInfo,
        }),
      );
    }

    // 영상 저장 클릭 로그
    let clickLogParam = `button_type=save_vod&title_no=${title_no}`;
    if (editMode !== 'section') {
      clickLogParam += `&editor_type=${editMode}_editor`;
    }
    setClickLog(clickLogParam);
  };

  return (
    <>
      {saveSuccessModal && (
        <ModalPortal>
          <Loading
            text={!data ? t('영상이 처리 중 입니다.') : data.MSG}
            isBtn={saveSuccessModal}
          />
        </ModalPortal>
      )}
      <Title
        editMode={editMode}
        titleInfo={response.info}
        modal={modal}
        loading={loading}
        webLoading={webLoading}
        saveBtn={saveBtn}
        webEditSaveInfo={webEditSaveInfo}
        setWebEditSaveInfo={setWebEditSaveInfo}
        onClickModal={onClickModal}
        onClickSaveCancel={onClickSaveCancel}
        onClickSave={onClickSave}
      />
    </>
  );
};

export default TitleContainer;
